/**
 * WebSocket统一管理器
 * 负责管理所有WebSocket连接，避免重复连接和内存泄漏
 */

import config from '../api/config';
import util from '../util';
import logger from '../util/logger';

class WebSocketManager {
    constructor() {
        // 存储所有活动的WebSocket连接
        this.connections = new Map();
        // 存储心跳定时器
        this.heartbeatTimers = new Map();
        // 存储重连定时器
        this.reconnectTimers = new Map();
        // 存储用户ID更新定时器
        this.userIdTimers = new Map();
        // 重连次数记录
        this.reconnectAttempts = new Map();
        // 最大重连次数
        this.maxReconnectAttempts = 10;
        // 重连延迟(毫秒)
        this.reconnectDelay = 3000;
        // 心跳间隔(毫秒)
        this.heartbeatInterval = 30000;
        // 用户ID更新间隔(毫秒)
        this.userIdUpdateInterval = 5000;
    }

    /**
     * 连接WebSocket
     * @param {string} channel - 频道名称 (如: 'trade', 'contract', 'option')
     * @param {Object} options - 配置选项
     * @param {string} options.url - WebSocket URL (可选，默认自动生成)
     * @param {Function} options.onMessage - 消息处理回调
     * @param {Function} options.onOpen - 连接成功回调
     * @param {Function} options.onError - 错误处理回调
     * @param {Function} options.onClose - 连接关闭回调
     * @param {number} options.userId - 用户ID (可选)
     * @param {boolean} options.enableHeartbeat - 是否启用心跳 (默认true)
     * @param {boolean} options.enableUserIdUpdate - 是否启用用户ID更新 (默认true)
     * @returns {WebSocket} WebSocket实例
     */
    connect(channel, options = {}) {
        // 如果已存在连接，先断开
        if (this.connections.has(channel)) {
            logger.warn(`⚠️ WebSocket频道 ${channel} 已存在，先断开旧连接`);
            this.disconnect(channel);
        }

        // 生成WebSocket URL
        const url = options.url || this.generateUrl(channel);
        
        logger.log(`🔌 正在连接WebSocket: ${channel}`, { url });

        try {
            const ws = new WebSocket(url);
            
            // 保存连接
            this.connections.set(channel, ws);
            
            // 重置重连次数
            this.reconnectAttempts.set(channel, 0);

            // 连接成功
            ws.onopen = () => {
                logger.log(`✅ WebSocket连接成功: ${channel}`);
                
                // 启动心跳
                if (options.enableHeartbeat !== false) {
                    this.startHeartbeat(channel, ws);
                }
                
                // 启动用户ID更新
                if (options.enableUserIdUpdate !== false && options.userId) {
                    this.startUserIdUpdate(channel, ws, options.userId);
                }
                
                // 调用用户回调
                if (options.onOpen) {
                    options.onOpen(ws);
                }
            };

            // 接收消息
            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    
                    // 忽略心跳响应
                    if (data.event === 'pong') {
                        return;
                    }
                    
                    logger.debug(`📨 WebSocket收到消息: ${channel}`, {
                        type: data.type,
                        event: data.event
                    });
                    
                    // 调用用户回调
                    if (options.onMessage) {
                        options.onMessage(data);
                    }
                } catch (error) {
                    logger.error(`❌ WebSocket消息解析失败: ${channel}`, error);
                }
            };

            // 连接错误
            ws.onerror = (error) => {
                logger.error(`❌ WebSocket连接错误: ${channel}`, error);
                
                // 调用用户回调
                if (options.onError) {
                    options.onError(error);
                }
            };

            // 连接关闭
            ws.onclose = (event) => {
                logger.log(`🔌 WebSocket连接关闭: ${channel}`, {
                    code: event.code,
                    reason: event.reason
                });
                
                // 清理资源
                this.cleanup(channel);
                
                // 调用用户回调
                if (options.onClose) {
                    options.onClose(event);
                }
                
                // 自动重连
                this.reconnect(channel, options);
            };

            return ws;
        } catch (error) {
            logger.error(`❌ WebSocket创建失败: ${channel}`, error);
            throw error;
        }
    }

    /**
     * 断开WebSocket连接
     * @param {string} channel - 频道名称
     */
    disconnect(channel) {
        const ws = this.connections.get(channel);
        
        if (ws) {
            logger.log(`🔌 断开WebSocket连接: ${channel}`);
            
            // 清理所有定时器
            this.cleanup(channel);
            
            // 关闭连接
            if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
                ws.close();
            }
            
            // 移除连接
            this.connections.delete(channel);
            this.reconnectAttempts.delete(channel);
        }
    }

    /**
     * 断开所有连接
     */
    disconnectAll() {
        logger.log('🔌 断开所有WebSocket连接');
        
        const channels = Array.from(this.connections.keys());
        channels.forEach(channel => {
            this.disconnect(channel);
        });
    }

    /**
     * 发送消息
     * @param {string} channel - 频道名称
     * @param {Object} data - 要发送的数据
     * @returns {boolean} 是否发送成功
     */
    send(channel, data) {
        const ws = this.connections.get(channel);
        
        if (!ws || ws.readyState !== WebSocket.OPEN) {
            logger.warn(`⚠️ WebSocket未连接或未就绪: ${channel}`);
            return false;
        }

        try {
            const message = typeof data === 'string' ? data : JSON.stringify(data);
            ws.send(message);
            logger.debug(`📤 WebSocket发送消息: ${channel}`, data);
            return true;
        } catch (error) {
            logger.error(`❌ WebSocket发送消息失败: ${channel}`, error);
            return false;
        }
    }

    /**
     * 获取连接状态
     * @param {string} channel - 频道名称
     * @returns {number|null} WebSocket状态码
     */
    getState(channel) {
        const ws = this.connections.get(channel);
        return ws ? ws.readyState : null;
    }

    /**
     * 检查是否已连接
     * @param {string} channel - 频道名称
     * @returns {boolean}
     */
    isConnected(channel) {
        const ws = this.connections.get(channel);
        return ws && ws.readyState === WebSocket.OPEN;
    }

    /**
     * 生成WebSocket URL
     * @param {string} channel - 频道名称
     * @returns {string}
     */
    generateUrl(channel) {
        const deviceId = util.getCache('deviceId');
        const baseUrl = config.BASE_WSS;
        
        // 根据频道类型生成不同的URL
        switch (channel) {
            case 'trade':
            case 'spot':
                return `${baseUrl}/?token=${deviceId}$trade&sub=trade`;
            case 'contract':
            case 'futures':
                return `${baseUrl}/?token=${deviceId}$contract&sub=contract`;
            case 'option':
            case 'flash':
                return `${baseUrl}/?token=${deviceId}$trade&sub=trade`;
            default:
                return `${baseUrl}/?token=${deviceId}$${channel}&sub=${channel}`;
        }
    }

    /**
     * 启动心跳
     * @param {string} channel - 频道名称
     * @param {WebSocket} ws - WebSocket实例
     */
    startHeartbeat(channel, ws) {
        // 清除旧的心跳定时器
        this.stopHeartbeat(channel);
        
        const timer = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
                try {
                    ws.send(JSON.stringify({
                        event: 'ping',
                        timestamp: Date.now()
                    }));
                    logger.debug(`💓 发送心跳: ${channel}`);
                } catch (error) {
                    logger.error(`❌ 发送心跳失败: ${channel}`, error);
                    this.stopHeartbeat(channel);
                }
            }
        }, this.heartbeatInterval);
        
        this.heartbeatTimers.set(channel, timer);
        logger.debug(`💓 启动心跳: ${channel}`);
    }

    /**
     * 停止心跳
     * @param {string} channel - 频道名称
     */
    stopHeartbeat(channel) {
        const timer = this.heartbeatTimers.get(channel);
        if (timer) {
            clearInterval(timer);
            this.heartbeatTimers.delete(channel);
            logger.debug(`💓 停止心跳: ${channel}`);
        }
    }

    /**
     * 启动用户ID更新
     * @param {string} channel - 频道名称
     * @param {WebSocket} ws - WebSocket实例
     * @param {number} userId - 用户ID
     */
    startUserIdUpdate(channel, ws, userId) {
        // 清除旧的定时器
        this.stopUserIdUpdate(channel);
        
        // 延迟1.5秒后开始定时更新
        setTimeout(() => {
            const timer = setInterval(() => {
                if (ws.readyState === WebSocket.OPEN && userId) {
                    try {
                        ws.send(JSON.stringify({
                            event: 'updateUserId',
                            uid: userId
                        }));
                        logger.debug(`👤 更新用户ID: ${channel}`, userId);
                    } catch (error) {
                        logger.error(`❌ 更新用户ID失败: ${channel}`, error);
                        this.stopUserIdUpdate(channel);
                    }
                }
            }, this.userIdUpdateInterval);
            
            this.userIdTimers.set(channel, timer);
            logger.debug(`👤 启动用户ID更新: ${channel}`);
        }, 1500);
    }

    /**
     * 停止用户ID更新
     * @param {string} channel - 频道名称
     */
    stopUserIdUpdate(channel) {
        const timer = this.userIdTimers.get(channel);
        if (timer) {
            clearInterval(timer);
            this.userIdTimers.delete(channel);
            logger.debug(`👤 停止用户ID更新: ${channel}`);
        }
    }

    /**
     * 重连
     * @param {string} channel - 频道名称
     * @param {Object} options - 原始配置选项
     */
    reconnect(channel, options) {
        // 获取重连次数
        const attempts = this.reconnectAttempts.get(channel) || 0;
        
        // 检查是否超过最大重连次数
        if (attempts >= this.maxReconnectAttempts) {
            logger.error(`❌ WebSocket重连失败，已达最大重连次数: ${channel}`);
            return;
        }
        
        // 清除旧的重连定时器
        const oldTimer = this.reconnectTimers.get(channel);
        if (oldTimer) {
            clearTimeout(oldTimer);
        }
        
        // 计算重连延迟(指数退避)
        const delay = this.reconnectDelay * Math.pow(1.5, attempts);
        
        logger.log(`🔄 ${delay/1000}秒后重连WebSocket: ${channel} (第${attempts + 1}次)`);
        
        const timer = setTimeout(() => {
            // 增加重连次数
            this.reconnectAttempts.set(channel, attempts + 1);
            
            // 重新连接
            this.connect(channel, options);
            
            // 清除定时器
            this.reconnectTimers.delete(channel);
        }, delay);
        
        this.reconnectTimers.set(channel, timer);
    }

    /**
     * 清理资源
     * @param {string} channel - 频道名称
     */
    cleanup(channel) {
        // 停止心跳
        this.stopHeartbeat(channel);
        
        // 停止用户ID更新
        this.stopUserIdUpdate(channel);
        
        // 清除重连定时器
        const reconnectTimer = this.reconnectTimers.get(channel);
        if (reconnectTimer) {
            clearTimeout(reconnectTimer);
            this.reconnectTimers.delete(channel);
        }
    }

    /**
     * 获取所有活动连接
     * @returns {Array} 连接列表
     */
    getActiveConnections() {
        const connections = [];
        this.connections.forEach((ws, channel) => {
            connections.push({
                channel,
                state: ws.readyState,
                stateText: this.getStateText(ws.readyState)
            });
        });
        return connections;
    }

    /**
     * 获取状态文本
     * @param {number} state - WebSocket状态码
     * @returns {string}
     */
    getStateText(state) {
        switch (state) {
            case WebSocket.CONNECTING:
                return '连接中';
            case WebSocket.OPEN:
                return '已连接';
            case WebSocket.CLOSING:
                return '关闭中';
            case WebSocket.CLOSED:
                return '已关闭';
            default:
                return '未知';
        }
    }
}

// 导出单例
export default new WebSocketManager();
